import L from 'leaflet';
import 'leaflet-rotate';
import { loadState, saveState } from '@iso/lib/helpers/localStorage';

/**
 * Map furniture — the north arrow and scale bar carried by every map, and the
 * map rotation they describe.
 *
 * Registered as a Leaflet init hook rather than a React component so one place
 * covers every map in the app: react-leaflet pages, imperative `L.map()` maps
 * (Activity/ActivityMap.js), and any map added later. Mirrors how Leaflet adds
 * its own zoom and attribution controls.
 *
 * leaflet-rotate gives Leaflet a bearing, so the arrow is a live compass: it
 * follows the map and clicking it returns the map to north-up. Rotate with
 * shift + mouse wheel, or a two-finger twist on touch.
 *
 * Opt a map out with `mapFurniture={false}`, or tune it per map, e.g.
 * `mapFurniture={{ northArrow: false, position: 'bottomleft' }}`. Opt out of
 * rotation alone with `rotate={false}`. react-leaflet forwards unknown props
 * into the Leaflet map options, so both arrive here as map options.
 */

const NORTH_ARROW_SRC = process.env.PUBLIC_URL + '/north-arrow.png';

const SCALE_MAX_WIDTH = 110;

const SCALE_TITLE = 'Click to switch between metric and imperial';

// Remembers a scale-bar unit the user picked by clicking, so the choice follows
// them across maps and sessions the way it does on Google Maps.
const SCALE_UNIT_KEY = 'airagri.mapScaleUnit';

// bottomright matches where Google Maps puts its compass, and keeps clear of
// the topright/topleft corners where pages stack their own dropdowns. It also
// avoids the bottom-left, where the Google logo the satellite layer draws sat
// on top of the scale bar and hid it completely.
const DEFAULTS = {
  northArrow: true,
  scaleBar: true,
  position: 'bottomright',
  arrowSize: 32,
  imperial: false,
};

const configFor = (map) => ({ ...DEFAULTS, ...(map.options.mapFurniture || {}) });

/** The clicked-and-remembered unit: true, false, or null when never set. */
const storedImperial = () => {
  const stored = loadState(SCALE_UNIT_KEY);
  return stored === undefined ? null : stored === 'imperial';
};

/**
 * A remembered click wins over any per-map default, so one explicit choice
 * holds everywhere. Without one, each map keeps its own default (metric, or a
 * property's acreage unit).
 */
export const effectiveImperial = (fallback) => {
  const stored = storedImperial();
  return stored === null ? !!fallback : stored;
};

/**
 * A property's acreage unit mapped to a scale-bar unit system. Hectares give
 * metric (m/km); acres and yards give imperial (ft/mi). Metric until loaded.
 */
export const isImperialAcreageUnit = (unit) =>
  ['acres', 'yard'].includes(String(unit).toLowerCase());

// ── North arrow ────────────────────────────────────────────────────────

const buildArrowElement = (size) => {
  const container = L.DomUtil.create('div', 'airagri-north-arrow');
  container.title = 'North';
  const img = L.DomUtil.create('img', '', container);
  img.src = NORTH_ARROW_SRC;
  img.alt = 'North';
  img.width = size;
  img.height = size;
  img.draggable = false;
  return container;
};

// ── Scale bar ──────────────────────────────────────────────────────────

// Open maps, so a click on one scale bar can retune all of them at once.
const liveLeafletMaps = new Set();
const liveMapboxScales = new Set();

const IMPERIAL_CLASS = 'airagri-scale--imperial';
const METRIC_CLASS = 'airagri-scale--metric';

/**
 * Both unit lines are built and kept up to date by Leaflet; CSS shows one.
 *
 * Switching units by rebuilding the control instead would re-append it to its
 * map corner, and since Leaflet stacks a corner in DOM order, the bar would
 * jump position on every click.
 */
const buildScaleElement = (map, position, imperial) => {
  const control = L.control
    .scale({ position, maxWidth: SCALE_MAX_WIDTH, metric: true, imperial: true })
    .addTo(map);

  const element = control.getContainer();
  L.DomUtil.addClass(element, 'airagri-scale');
  L.DomUtil.addClass(element, imperial ? IMPERIAL_CLASS : METRIC_CLASS);
  element.title = SCALE_TITLE;
  L.DomEvent.on(element, 'click', () => {
    toggleMapScaleUnit(!L.DomUtil.hasClass(element, IMPERIAL_CLASS));
  });

  return { control, element };
};

const applyScale = (map, imperial) => {
  const element = map && map._airagriScaleElement;
  if (!element) return;

  L.DomUtil.removeClass(element, imperial ? METRIC_CLASS : IMPERIAL_CLASS);
  L.DomUtil.addClass(element, imperial ? IMPERIAL_CLASS : METRIC_CLASS);
};

// ── The stack ──────────────────────────────────────────────────────────

/**
 * Arrow and scale bar in one control, as a flex column.
 *
 * They share a container rather than sitting in the corner as two controls
 * because Leaflet lays a corner out in DOM order — which for a react-leaflet
 * map depends on whether a control came from an init hook or from a child
 * component, so "scale directly below the arrow" would hinge on mount timing.
 * Inside one container the order is structural.
 */
L.Control.MapFurniture = L.Control.extend({
  options: { ...DEFAULTS },

  onAdd: function (map) {
    const container = L.DomUtil.create('div', 'airagri-map-furniture');
    // Keeps clicks (including the unit toggle) from panning/zooming the map.
    L.DomEvent.disableClickPropagation(container);

    if (this.options.northArrow) {
      const arrow = buildArrowElement(this.options.arrowSize);
      container.appendChild(arrow);

      // With rotation available the arrow is a compass rather than a label: it
      // tracks the bearing, drag turns the map, and a plain click faces north
      // again. Drag is the discoverable affordance — the plugin's own shift +
      // wheel gesture is not something anyone finds on their own.
      if (map.options.rotate) {
        this._arrow = arrow;
        this._arrowImage = arrow.getElementsByTagName('img')[0];
        arrow.title = 'Drag to rotate the map, click to face north';
        L.DomUtil.addClass(arrow, 'airagri-north-arrow--compass');
        L.DomEvent.on(arrow, 'mousedown', this._onCompassDown, this);
        L.DomEvent.on(arrow, 'click', this._onCompassClick, this);
        map.on('rotate', this._syncArrow, this);
        this._syncArrow();
      }
    }

    if (this.options.scaleBar) {
      // L.Control.Scale puts its container in a map corner; move it in here.
      // It only ever rewrites that node's width and text, so relocating is safe.
      const scale = buildScaleElement(map, this.options.position, this.options.imperial);
      this._scale = scale.control;
      map._airagriScaleElement = scale.element;
      container.appendChild(scale.element);
    }

    return container;
  },

  onRemove: function (map) {
    map.off('rotate', this._syncArrow, this);
    // A map torn down mid-drag would otherwise leave these on the document.
    L.DomEvent.off(document, 'mousemove', this._onCompassMove, this);
    L.DomEvent.off(document, 'mouseup', this._onCompassUp, this);
    this._arrowImage = null;
    this._arrow = null;

    if (this._scale) {
      map.removeControl(this._scale);
      this._scale = null;
    }
    delete map._airagriScaleElement;
  },

  /** Same transform direction the plugin's own rotate control uses. */
  _syncArrow: function () {
    if (!this._arrowImage || !this._map) return;
    this._arrowImage.style.transform = `rotate(${this._map.getBearing()}deg)`;
  },

  /** Pointer angle around the arrow's centre, in degrees. */
  _pointerAngle: function (e) {
    const box = this._arrow.getBoundingClientRect();
    const dx = e.clientX - (box.left + box.width / 2);
    const dy = e.clientY - (box.top + box.height / 2);
    return (Math.atan2(dy, dx) * 180) / Math.PI;
  },

  _onCompassDown: function (e) {
    L.DomEvent.stop(e);
    this._dragAngle = this._pointerAngle(e);
    this._dragBearing = this._map.getBearing();
    this._dragged = false;
    L.DomUtil.addClass(this._arrow, 'airagri-north-arrow--dragging');
    L.DomEvent.on(document, 'mousemove', this._onCompassMove, this);
    L.DomEvent.on(document, 'mouseup', this._onCompassUp, this);
  },

  _onCompassMove: function (e) {
    this._dragged = true;
    this._map.setBearing(this._dragBearing + this._pointerAngle(e) - this._dragAngle);
  },

  _onCompassUp: function () {
    L.DomUtil.removeClass(this._arrow, 'airagri-north-arrow--dragging');
    L.DomEvent.off(document, 'mousemove', this._onCompassMove, this);
    L.DomEvent.off(document, 'mouseup', this._onCompassUp, this);
  },

  /** A click that was not the end of a drag means "face north again". */
  _onCompassClick: function (e) {
    L.DomEvent.stop(e);
    if (!this._dragged && this._map) this._map.setBearing(0);
  },
});

/**
 * Flip every open map's scale bar between metric and imperial and remember the
 * choice — what clicking Google Maps' scale bar does.
 *
 * On-path measurement labels follow the same switch. Their plugin copies its
 * options when a layer is shown, so the labels only pick up a new unit when
 * redrawn — hence the explicit refresh. `updateMeasurements` guards on having
 * a measurement layer, so calling it across every layer is safe.
 */
export const toggleMapScaleUnit = (imperial) => {
  saveState(SCALE_UNIT_KEY, imperial ? 'imperial' : 'metric');
  liveMapboxScales.forEach((scale) => scale.setUnit(imperial ? 'imperial' : 'metric'));
  liveLeafletMaps.forEach((map) => {
    applyScale(map, imperial);
    map.eachLayer((layer) => {
      if (typeof layer.updateMeasurements === 'function') layer.updateMeasurements();
    });
  });
};

/**
 * Point a map's scale bar at a property's unit system.
 *
 * Needed because the control is built when the map is created, but a property's
 * acreage unit only arrives later from the API — and remounting the map to pick
 * up a new option would reset the view and reload every layer. A unit the user
 * picked by clicking still wins.
 */
export const setMapScaleUnit = (map, imperial) => {
  applyScale(map, effectiveImperial(imperial));
};

/**
 * Give a Mapbox map's scale bar the same click-to-toggle behaviour. Mapbox
 * supplies no click handler of its own but does expose `setUnit()`.
 */
export const attachMapboxScaleToggle = (mapboxMap, scaleControl) => {
  if (!mapboxMap || !scaleControl) return;

  const container = mapboxMap.getContainer().querySelector('.mapboxgl-ctrl-scale');
  if (!container) return;

  const startImperial = effectiveImperial(false);
  scaleControl.setUnit(startImperial ? 'imperial' : 'metric');

  container.title = SCALE_TITLE;
  container.style.cursor = 'pointer';
  container.addEventListener('click', () => {
    toggleMapScaleUnit(scaleControl.options.unit !== 'imperial');
  });

  liveMapboxScales.add(scaleControl);
  mapboxMap.on('remove', () => liveMapboxScales.delete(scaleControl));
};

/**
 * Google Maps draws its own scale bar via the `scaleControl` option but has no
 * north arrow, so push one in as a custom control. Call from `onReady`.
 *
 * Uses RIGHT_BOTTOM (vertical stack up the right edge) to match where the
 * Leaflet maps put theirs, and to stay clear of the map-type control on top.
 */
export const attachGoogleNorthArrow = (map, google, size = DEFAULTS.arrowSize) => {
  if (!map || !google) return;
  map.controls[google.maps.ControlPosition.RIGHT_BOTTOM].push(buildArrowElement(size));
};

// Guard against a double registration adding two of each control.
if (!L.Map.airagriMapFurnitureRegistered) {
  L.Map.airagriMapFurnitureRegistered = true;

  // Turn leaflet-rotate on for every map.
  //
  // `rotate` CANNOT be switched on with mergeOptions. leaflet-rotate's
  // initialize reads `options.rotate` off the raw constructor argument, before
  // Leaflet has merged the prototype defaults in — so a prototype default is
  // invisible to it and rotation silently stays off. (The plugin then checks
  // `this.options.rotate`, which does see the default, and calls setBearing —
  // which returns early because the internal `_rotate` flag was never set. No
  // error, no rotation.) Injecting it into the options argument is what works.
  //
  // Spreading `options` last leaves an explicit `rotate={false}` intact.
  const rotateInitialize = L.Map.prototype.initialize;
  L.Map.include({
    initialize: function (id, options) {
      rotateInitialize.call(this, id, { rotate: true, ...options });
    },
  });

  // `rotateControl: false` because the plugin would otherwise add a second
  // compass to every map — our north arrow is the compass. `shiftKeyRotate` is
  // already true by default and gives desktop shift + wheel rotation;
  // `touchRotate` adds the two-finger twist on tablets and phones. These are
  // read after the merge, so mergeOptions is fine for them.
  L.Map.mergeOptions({
    rotateControl: false,
    touchRotate: true,
  });

  L.Map.addInitHook(function () {
    if (this.options.mapFurniture === false) return;
    const cfg = configFor(this);
    if (!cfg.northArrow && !cfg.scaleBar) return;

    new L.Control.MapFurniture({
      ...cfg,
      imperial: effectiveImperial(cfg.imperial),
    }).addTo(this);

    if (cfg.scaleBar) {
      liveLeafletMaps.add(this);
      this.on('unload', () => liveLeafletMaps.delete(this));
    }
  });
}
